import { useEffect, useMemo, useState } from "react";
import { Row, Card, CardBody, Table } from "reactstrap";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

import { CustomButton } from "components/CustomButton";
import { getMcapImplementationSummary, getMcapImplementationReport, getMcapOrgTotals } from "api/api.service";
import { getCached, setCached, mcapCacheKey } from "utils/mcapCache";
import KpiTile from "../components/KpiTile";
import MomentDetailModal, { SINGLE_MOMENT } from "../components/MomentDetailModal";
import SchoolDetailPanels from "../components/SchoolDetailPanels";
import { CONTEXT_LABELS, STATE_LABELS, GRADE_LABELS, BUCKET_LABELS, BUCKET_COLORS, BAND_COLORS } from "../labels";
import DonutChart from "../components/DonutChart";
import typography from "../typography";

/**
 * Implementation fidelity and instructional time.
 *
 * SCOPE — WHY THESE FOUR TILES AND NOT THE OBVIOUS ONES
 * Active classrooms, active teachers and total resets are Section A item A1,
 * built separately. Repeating them here would give an administrator two tiles
 * for the same number on two tabs, and the first time those disagreed — one
 * cached, one not — they would stop trusting both. Everything on this tab is
 * something A1 does not answer: what the resets bought back in classroom time,
 * how fast classes actually returned to work, whether resets have become part
 * of the routine, and which classrooms have not got there yet.
 *
 * THREE RULES THIS SCREEN HOLDS
 *
 *  1. A suppressed metric is never rendered as zero. Null means "too few
 *     records to report", which is a different and far better claim than
 *     "none".
 *  2. The minutes-regained figure never appears without its assumption. It no
 *     longer sits in a block below the number — it is on the tile itself,
 *     under an info control, so the caveat travels with the figure without
 *     spending a fifth of the card on it.
 *  3. Nothing here identifies a teacher or a student, including in the
 *     drill-down. Classroom level, always.
 */

const panelVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.23, 1, 0.32, 1] } },
};

const BUCKET_ORDER = ["UNDER_1_MIN", "ONE_TO_TWO_MIN", "THREE_TO_FIVE_MIN", "FIVE_PLUS_MIN"];

export default function Implementation({ filters, filterBar }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [openMoment, setOpenMoment] = useState(null);
  const [sortBy, setSortBy] = useState("resets");
  // The school-by-school comparison table, moved here from Overview: this is
  // the schools drill-down tab, so "which school looks different from the
  // rest" belongs beside the per-school playback/activity panels below it,
  // not on the platform-wide summary screen. Same org-totals call Overview
  // itself still makes for its own hero cards — this tab just also needs
  // the bySchool rows out of that same response.
  const [orgData, setOrgData] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("implementation", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getMcapImplementationSummary(filters)
      .then((res) => {
        if (cancelled) return;
        setData(res || null);
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    let cancelled = false;
    // Same cache key Overview's own org-totals fetch uses — a tab switch
    // within the TTL reuses the response instead of refetching it.
    const cacheKey = mcapCacheKey("org-totals", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setOrgData(cached);
      setOrgLoading(false);
      return undefined;
    }
    setOrgLoading(true);
    getMcapOrgTotals(filters)
      .then((res) => {
        if (cancelled) return;
        setOrgData(res || null);
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setOrgData(null);
      })
      .finally(() => {
        if (!cancelled) setOrgLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const blob = await getMcapImplementationReport(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `implementation-summary-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoking immediately can cancel the download in some browsers; one tick
      // after the click is enough and the blob is not leaked.
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error(typography.IMPL_DOWNLOAD_ERROR);
    } finally {
      setDownloading(false);
    }
  };

  const totalOutcomes = data?.outcomesWithReturnToTask ?? 0;
  const spread = data?.contextSpread ?? [];
  const gradeBands = data?.gradeBands ?? [];

  // Classrooms still using resets at a single moment — the coaching list, and
  // the one number on this tab that names work to do rather than work done.
  const stillTryingOut = spread.find((s) => s.contexts === 1)?.classrooms ?? 0;

  // Both memos read `data` rather than the `?? []` locals above: a fallback
  // literal is a new array on every render, so depending on one would recompute
  // every time and defeat the memo entirely.
  const byBucket = useMemo(() => {
    const map = new Map((data?.returnToTaskDistribution ?? []).map((d) => [d.bucket, d.count]));
    return BUCKET_ORDER.map((bucket) => ({ bucket, count: map.get(bucket) ?? 0 }));
  }, [data]);

  const sortedMoments = useMemo(() => {
    const rows = [...(data?.momentBreakdown ?? [])];
    if (sortBy === "classrooms") return rows.sort((a, b) => b.classrooms - a.classrooms);
    if (sortBy === "moment") return rows.sort((a, b) => (CONTEXT_LABELS[a.context] || a.context).localeCompare(CONTEXT_LABELS[b.context] || b.context));
    return rows.sort((a, b) => b.resets - a.resets);
  }, [data, sortBy]);

  const maxMomentResets = sortedMoments.reduce((m, r) => Math.max(m, r.resets), 0) || 1;
  const activeClassroomTotal = gradeBands.reduce((t, g) => t + g.classrooms, 0);

  const bySchool = orgData?.bySchool ?? [];
  // Same reasoning the table carried on Overview: a school/section filter
  // already narrows everything above to that one school, so the comparison
  // table would just repeat that single row back.
  const showSchoolTable = bySchool.length > 1;


  return (
    <div className="p-4 mt-3">
      {filterBar}

      <Row>
        <KpiTile
          index={0}
          label={typography.IMPL_FIG_MINUTES}
          value={data?.timeSavedMinutes}
          // The same shape the teacher's own Class Insights uses: the saving
          // against what those transitions would otherwise have cost. A bare
          // "1555" says nothing about how much there was to save.
          suffix={data?.timeWithoutResetsMinutes != null ? `of ${data.timeWithoutResetsMinutes}` : undefined}
          caption={typography.IMPL_CAP_MINUTES}
          loading={loading}
          colorClass="primary-gradient-bg"
          unavailableLabel={typography.IMPL_NOT_ENOUGH}
          note={data?.assumptionNote}
        />
        <KpiTile
          index={1}
          label={typography.IMPL_FIG_UNDER_TWO}
          value={data?.pctUnderTwoMinutes}
          unit="%"
          caption={typography.IMPL_CAP_UNDER_TWO}
          loading={loading}
          colorClass="bg-blue"
          unavailableLabel={typography.IMPL_NOT_ENOUGH}
          note={typography.IMPL_NOTE_UNDER_TWO}
        />
        <KpiTile
          index={2}
          label={typography.IMPL_FIG_PLAYBACK}
          value={data?.playbackCompletionRate}
          unit="%"
          caption={
            data?.playbackSessions ? `of ${data.playbackSessions} played resets` : typography.IMPL_CAP_PLAYBACK
          }
          loading={loading}
          colorClass="bg-purple"
          unavailableLabel={typography.IMPL_NOT_ENOUGH}
          note={typography.IMPL_NOTE_PLAYBACK}
        />
        <KpiTile
          index={3}
          label={typography.IMPL_FIG_STILL_TRYING}
          value={loading ? null : stillTryingOut}
          caption={typography.IMPL_CAP_STILL_TRYING}
          loading={loading}
          colorClass="bg-orange"
          note={typography.IMPL_NOTE_STILL_TRYING}
          onClick={stillTryingOut > 0 ? () => setOpenMoment(SINGLE_MOMENT) : undefined}
          actionLabel={stillTryingOut > 0 ? typography.IMPL_SEE_CLASSROOMS : undefined}
        />
      </Row>

      <motion.div initial="hidden" animate="visible" variants={panelVariants} className="mcap-impl-panels">
        <div className="mcap-impl-card">
          <div className="mcap-impl-card-head">
            <div>
              <h5 className="mcap-impl-card-title mb-0">{typography.IMPL_RTT_TITLE}</h5>
              <p className="standard-typo-12-400-ld mb-0">{typography.IMPL_RTT_DETAIL}</p>
            </div>
            <span className="standard-typo-12-400-ld">{loading ? "" : `${totalOutcomes} recorded`}</span>
          </div>

          {!loading && totalOutcomes === 0 ? (
            <p className="standard-typo-14-400-ld mcap-impl-empty">{typography.IMPL_NOT_ENOUGH_DETAIL}</p>
          ) : (
            // A composition of four mutually exclusive buckets summing to the
            // whole — the one shape a donut is actually the right chart for.
            // The hole carries the headline so the ring does not need decoding.
            <DonutChart
              segments={byBucket.map(({ bucket, count }) => ({
                key: bucket,
                label: BUCKET_LABELS[bucket],
                value: count,
                color: BUCKET_COLORS[bucket],
              }))}
              centerValue={data?.pctUnderTwoMinutes != null ? `${data.pctUnderTwoMinutes}%` : "—"}
              centerLabel={typography.IMPL_DONUT_CENTER}
            />
          )}
        </div>

        <div className="mcap-impl-card">
          <div className="mcap-impl-card-head">
            <div>
              <h5 className="mcap-impl-card-title mb-0">{typography.IMPL_BANDS_TITLE}</h5>
              <p className="standard-typo-12-400-ld mb-0">{typography.IMPL_BANDS_DETAIL}</p>
            </div>
          </div>

          {!loading && gradeBands.length === 0 ? (
            <p className="standard-typo-14-400-ld mcap-impl-empty">{typography.IMPL_SPREAD_EMPTY}</p>
          ) : (
            <DonutChart
              segments={gradeBands.map(({ band, classrooms }, i) => ({
                key: band,
                label: GRADE_LABELS[band] || band,
                value: classrooms,
                color: BAND_COLORS[i % BAND_COLORS.length],
              }))}
              centerValue={activeClassroomTotal}
              // A school with one classroom read "1 active classrooms".
              centerLabel={`active classroom${activeClassroomTotal === 1 ? "" : "s"}`}
            />
          )}
        </div>
      </motion.div>

      {/* The table an administrator can act on: one row per reset moment, and
          every row opens the classrooms behind it. */}
      <motion.div initial="hidden" animate="visible" variants={panelVariants} className="mcap-impl-card mcap-impl-wide">
        <div className="mcap-impl-card-head">
          <div>
            <h5 className="mcap-impl-card-title mb-0">{typography.IMPL_MOMENTS_TITLE}</h5>
            <p className="standard-typo-12-400-ld mb-0">{typography.IMPL_MOMENTS_DETAIL}</p>
          </div>
          <div className="mcap-sort">
            <span className="standard-typo-12-400-ld">Sort</span>
            {[
              { key: "resets", label: "Resets" },
              { key: "classrooms", label: "Classrooms" },
              { key: "moment", label: "A–Z" },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`mcap-sort-btn${sortBy === key ? " is-active" : ""}`}
                onClick={() => setSortBy(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {!loading && sortedMoments.length === 0 ? (
          <p className="standard-typo-14-400-ld mcap-impl-empty">{typography.IMPL_CONTEXT_EMPTY}</p>
        ) : (
          <table className="mcap-moments">
            <thead>
              <tr>
                <th>Reset moment</th>
                <th className="mcap-moments-barcol">Resets</th>
                <th className="text-end">Classrooms</th>
                <th>Most common need</th>
                <th className="text-end" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {sortedMoments.map((row) => (
                <tr
                  key={row.context}
                  className="mcap-moments-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenMoment(row.context)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenMoment(row.context);
                    }
                  }}
                >
                  <td className="mcap-moments-name">{CONTEXT_LABELS[row.context] || row.context}</td>
                  <td className="mcap-moments-barcol">
                    <span className="mcap-moments-bar">
                      <span className="mcap-moments-bar-fill" style={{ width: `${(row.resets / maxMomentResets) * 100}%` }} />
                    </span>
                    <span className="mcap-moments-bar-num">{row.resets}</span>
                  </td>
                  <td className="text-end mcap-moments-num">{row.classrooms}</td>
                  <td>
                    {row.topState ? (
                      <span className="mcap-chip">{STATE_LABELS[row.topState] || row.topState}</span>
                    ) : (
                      <span className="mcap-moments-muted">{typography.IMPL_NOT_ENOUGH}</span>
                    )}
                  </td>
                  <td className="text-end mcap-moments-open">
                    <i className="mdi mdi-chevron-right" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </motion.div>

      {/* The entry point for the drill-down below: which schools look
          different from the rest, at a glance, before picking one. Moved
          here from Overview — that screen is the platform-wide summary, this
          one is the schools tab, and the two were competing for the same
          table. */}
      {showSchoolTable && (
        <motion.div initial="hidden" animate="visible" variants={panelVariants}>
          <Card className="mt-3">
            <CardBody>
              <h5 className="mb-1 font-family-lexend standard-typo-14-500-ld">{typography.OVERVIEW_BY_SCHOOL_TITLE}</h5>
              <div className="table-responsive mcap-premium-table-wrap">
                <Table borderless className="mcap-premium-table mb-0">
                  <thead>
                    <tr>
                      <th>{typography.OVERVIEW_COL_SCHOOL}</th>
                      <th>{typography.OVERVIEW_COL_DISTRICT}</th>
                      <th>{typography.OVERVIEW_COL_CLASSROOMS}</th>
                      <th>{typography.OVERVIEW_COL_TEACHERS}</th>
                      <th>{typography.OVERVIEW_COL_COMPLETED}</th>
                      <th>{typography.OVERVIEW_COL_RESETS_PER_TEACHER}</th>
                      <th>{typography.OVERVIEW_COL_PCT_LOGGED}</th>
                      <th>{typography.OVERVIEW_COL_AVG_RATING}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bySchool.map((row) => (
                      <tr key={row.schoolId}>
                        <td>{row.schoolName}</td>
                        <td>{row.districtName || typography.OVERVIEW_NO_DISTRICT}</td>
                        {row.suppressed ? (
                          <td colSpan={6} className="text-muted">
                            {typography.OVERVIEW_SUPPRESSED}
                          </td>
                        ) : (
                          <>
                            <td>{row.activeClassrooms}</td>
                            <td>{row.activeTeachers}</td>
                            <td>{row.completedResets}</td>
                            <td>{row.resetsPerTeacher ?? "—"}</td>
                            <td>{row.pctWithLoggedOutcome != null ? `${row.pctWithLoggedOutcome}%` : "—"}</td>
                            <td>{row.avgUsefulness != null ? `${row.avgUsefulness}/10` : "—"}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      )}

      {/* Only when a school is selected. With no school picked the dashboard
          stays exactly as it is today — a playback funnel across every school
          answers nothing anyone can act on. */}
      <SchoolDetailPanels schoolId={filters?.schoolId} filters={filters} />

      <div className="mcap-impl-actions">
        {/* color="primary" — the className this borrowed (a leftover from a
            since-deleted page's own scoped styles) rendered near-white text
            on a near-white background: readable in the DOM, invisible on
            screen. */}
        <CustomButton
          title={downloading ? "Preparing…" : typography.IMPL_DOWNLOAD}
          color="primary"
          onClick={handleDownload}
          loading={downloading}
          disabled={downloading || loading}
        />
        <span className="standard-typo-12-400-ld">{typography.IMPL_DOWNLOAD_DETAIL}</span>
      </div>

      <MomentDetailModal context={openMoment} filters={filters} onClose={() => setOpenMoment(null)} />
    </div>
  );
}
